import { useEffect } from 'react';

const SITE = 'https://numwise.eu';
const BRAND = 'NumWise';
const DEFAULT_IMAGE = `${SITE}/og-image.png`;

interface PageMetaOptions {
  title: string;
  description: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  /** Keep this page out of search results (private, token or error pages). */
  noindex?: boolean;
}

type MetaTag =
  | { attr: 'name' | 'property'; key: string; content: string }
  | { attr: 'rel'; key: 'canonical'; href: string };

export interface PageMeta {
  title: string;
  /** Absolute canonical URL, or null for pages that have none (noindex, error and token pages). */
  canonical: string | null;
  noindex: boolean;
  tags: MetaTag[];
}

/** Every head tag the pages manage; the prerender removes these from the HTML template before writing a page's own. */
export const MANAGED_TAGS: { attr: MetaTag['attr']; key: string }[] = [
  { attr: 'name', key: 'description' },
  { attr: 'name', key: 'robots' },
  { attr: 'property', key: 'og:title' },
  { attr: 'property', key: 'og:description' },
  { attr: 'property', key: 'og:type' },
  { attr: 'property', key: 'og:url' },
  { attr: 'property', key: 'og:image' },
  { attr: 'name', key: 'twitter:card' },
  { attr: 'name', key: 'twitter:title' },
  { attr: 'name', key: 'twitter:description' },
  { attr: 'name', key: 'twitter:image' },
  { attr: 'rel', key: 'canonical' },
];

/** One rule for every page: the brand is appended unless the title already names NumWise. */
export function withBrand(title: string): string {
  return title.includes(BRAND) ? title : `${title} | ${BRAND}`;
}

export function buildPageMeta({
  title,
  description,
  canonicalPath,
  ogImage = DEFAULT_IMAGE,
  ogType = 'website',
  noindex = false,
}: PageMetaOptions): PageMeta {
  const fullTitle = withBrand(title);
  const canonical = canonicalPath && !noindex ? `${SITE}${canonicalPath}` : null;
  const tags: MetaTag[] = [
    { attr: 'name', key: 'description', content: description },
    ...(noindex ? [{ attr: 'name' as const, key: 'robots', content: 'noindex' }] : []),
    { attr: 'property', key: 'og:title', content: fullTitle },
    { attr: 'property', key: 'og:description', content: description },
    { attr: 'property', key: 'og:type', content: ogType },
    ...(canonical ? [{ attr: 'property' as const, key: 'og:url', content: canonical }] : []),
    { attr: 'property', key: 'og:image', content: ogImage },
    { attr: 'name', key: 'twitter:card', content: 'summary_large_image' },
    { attr: 'name', key: 'twitter:title', content: fullTitle },
    { attr: 'name', key: 'twitter:description', content: description },
    { attr: 'name', key: 'twitter:image', content: ogImage },
    ...(canonical ? [{ attr: 'rel' as const, key: 'canonical' as const, href: canonical }] : []),
  ];
  return { title: fullTitle, canonical, noindex, tags };
}

const escapeAttr = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** The same tags as static HTML, for the prerendered page's head. */
export function pageMetaHtml(meta: PageMeta): string {
  const tags = meta.tags.map((t) =>
    t.attr === 'rel'
      ? `<link rel="canonical" href="${escapeAttr(t.href)}" />`
      : `<meta ${t.attr}="${t.key}" content="${escapeAttr(t.content)}" />`
  );
  return [`<title>${escapeAttr(meta.title)}</title>`, ...tags].join('\n    ');
}

function applyPageMeta(meta: PageMeta) {
  document.title = meta.title;
  for (const managed of MANAGED_TAGS) {
    const tag = meta.tags.find((t) => t.attr === managed.attr && t.key === managed.key);
    const selector = managed.attr === 'rel' ? 'link[rel="canonical"]' : `meta[${managed.attr}="${managed.key}"]`;
    let el = document.head.querySelector(selector);
    if (!tag) {
      el?.remove();
      continue;
    }
    if (!el) {
      el = document.createElement(tag.attr === 'rel' ? 'link' : 'meta');
      el.setAttribute(tag.attr, tag.key);
      document.head.appendChild(el);
    }
    el.setAttribute(tag.attr === 'rel' ? 'href' : 'content', tag.attr === 'rel' ? tag.href : tag.content);
  }
}

let prerenderMeta: PageMeta | null = null;

function recordForPrerender(meta: PageMeta) {
  prerenderMeta = meta;
}

/** Called by the prerender after rendering a page: the meta that page asked for (effects do not run on the server). */
export function takePrerenderMeta(): PageMeta | null {
  const meta = prerenderMeta;
  prerenderMeta = null;
  return meta;
}

/**
 * Sets the document title and head tags for SEO and link previews: description, Open Graph, Twitter card,
 * canonical URL and robots. Tags a page does not ask for (a canonical on a noindex page) are removed.
 */
export function usePageMeta(options: PageMetaOptions) {
  const { title, description, canonicalPath, ogImage, ogType, noindex } = options;
  if (typeof document === 'undefined') recordForPrerender(buildPageMeta(options));

  useEffect(() => {
    applyPageMeta(buildPageMeta({ title, description, canonicalPath, ogImage, ogType, noindex }));
  }, [title, description, canonicalPath, ogImage, ogType, noindex]);
}
